
// components/Users/UserAutocomplete.tsx
import { Autocomplete, CircularProgress, InputAdornment, TextField, useTheme } from '@mui/material'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useMemo, useState } from 'react'
import { User } from 'types'
import { renderUserOption } from '../Team/renderOptions'

/** Slack-sourced users carry both; older records may only have `user_uuid`. */
const userIdOf = (user: User): string => user?.user_uuid ?? user?.id

const UserAutocomplete = ({
  value,
  onChange,
  context,
  multiple = false,
  label = 'Select User',
  isDirty = false,
  savingFields = [],
}) => {
  const theme = useTheme()
  const [inputValue, setInputValue] = useState('')
  const { data: users, isLoading } = useUserItems()

  // Read straight off the form value, as TeamRoleAutocomplete does. Keeping a
  // copy in state and refetching it by id reset the selection on every render,
  // so a picked owner cleared itself before the form saw it.
  const selected = useMemo(() => {
    const ids = Array.isArray(value) ? value : value ? [value] : []
    const found = ids.map(id => (users ?? []).find(u => userIdOf(u) === id)).filter((u): u is User => !!u)
    return multiple ? found : (found[0] ?? null)
  }, [users, value, multiple])

  const handleSelectUser = (_, newValue) => {
    if (multiple) {
      onChange(context, newValue.map(userIdOf))
    } else {
      onChange(context, newValue ? [userIdOf(newValue)] : [])
    }
  }

  if (isLoading) {
    return <CircularProgress />
  }

  return (
    <Autocomplete
      multiple={multiple}
      options={users || []}
      getOptionLabel={option =>
        option.real_name ||
        `${option.firstName || option.first_name} ${option.lastName || option.last_name} (${option.email})`
      }
      value={selected}
      onChange={handleSelectUser}
      inputValue={inputValue}
      onInputChange={(_, newVal) => setInputValue(newVal)}
      isOptionEqualToValue={(option, value) => userIdOf(option) === userIdOf(value)}
      filterOptions={(options, { inputValue }) =>
        options.filter(option =>
          `${option.firstName || option.first_name} ${option.lastName || option.last_name}`
            .toLowerCase()
            .includes(inputValue.toLowerCase())
        )
      }
      renderOption={(props, option, state) => renderUserOption(props, option, state, theme)}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps, // <-- this keeps the built-in adornments like clear icon
            endAdornment: (
              <>
                {savingFields.includes(context) && (
                  <InputAdornment position="end">
                    <CircularProgress size={16} thickness={5} sx={{ mr: 1 }} />
                  </InputAdornment>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      fullWidth
      sx={{
        '& .MuiInputBase-root': {
          bgcolor: isDirty ? theme.palette.text.dirty : theme.palette.background.input,
        },
      }}
    />
  )
}

export default UserAutocomplete
